import React, {useCallback, useRef, useState} from 'react';

// only guards absurd values, where nothing tells how short a runner can be
export const MIN_DRAG_HEIGHT = 20;

export const roundHeight = (value: number) => Math.round(value * 10) / 10;

export type FaceEdge = 'top' | 'bottom';

export interface FacePreview {
    dragged: number;
    draggedHeight: number;
    heights: number[];
}

export interface DragLimits {
    draggedHeight: number;
    min?: number;
    max: number;
}

interface UseDragGestureProps {
    // pixels per mm, which turns a pointer movement into a height
    ratio: number;
    limits: (dragged: number) => DragLimits;
    // where every face lands while the drag is in flight
    settle: (dragged: number, height: number) => number[];
    commit: (dragged: number, height: number) => void;
}

const resolve = (element: HTMLElement) => {
    const edge = element.dataset.edge as FaceEdge;

    return {
        edge,
        dragged: Number(element.dataset.index),
        direction: edge === 'bottom' ? 1 : -1,
    };
};

// The gesture of dragging a boundary between two faces. Nothing reaches the
// form until the drag ends; which faces move in the meantime is the caller's
// to say, so the drawers of a cabinet and the faces of a panel share this.
export const useDragGesture = ({
    ratio,
    limits,
    settle,
    commit,
}: UseDragGestureProps) => {
    const [dragging, setDragging] = useState<FaceEdge>(null);
    const [preview, setPreview] = useState<FacePreview>(null);

    // the listeners below outlive the render they were created in
    const previewRef = useRef<FacePreview>(null);

    const showPreview = useCallback((next: FacePreview) => {
        previewRef.current = next;
        setPreview(next);
    }, []);

    const measure = useCallback(
        (dragged: number, height: number): FacePreview => {
            const {min = MIN_DRAG_HEIGHT, max} = limits(dragged);
            const next = Math.max(min, Math.min(max, roundHeight(height)));

            return {
                dragged,
                draggedHeight: next,
                heights: settle(dragged, next),
            };
        },
        [limits, settle]
    );

    // pointer events rather than mouse, so a finger drags the same as a cursor
    const startDrag = useCallback(
        (event: React.PointerEvent<HTMLElement>) => {
            event.preventDefault();

            const {edge, dragged, direction} = resolve(event.currentTarget);
            const {draggedHeight} = limits(dragged);
            const startY = event.clientY;

            setDragging(edge);

            const move = (moveEvent: PointerEvent) => {
                showPreview(
                    measure(
                        dragged,
                        draggedHeight +
                            (direction * (moveEvent.clientY - startY)) / ratio
                    )
                );
            };

            const drop = () => {
                document.removeEventListener('pointermove', move);
                document.removeEventListener('pointerup', drop);
                document.removeEventListener('pointercancel', drop);
                setDragging(null);

                if (previewRef.current) {
                    commit(dragged, previewRef.current.draggedHeight);
                }

                showPreview(null);
            };

            document.addEventListener('pointermove', move);
            document.addEventListener('pointerup', drop);
            // a touch gesture the browser takes over ends as a cancel, not an up
            document.addEventListener('pointercancel', drop);
        },
        [limits, measure, ratio, commit, showPreview]
    );

    const nudge = useCallback(
        (event: React.KeyboardEvent<HTMLElement>) => {
            const step =
                event.key === 'ArrowUp'
                    ? -1
                    : event.key === 'ArrowDown'
                      ? 1
                      : 0;

            if (step === 0) return;

            event.preventDefault();

            const {dragged, direction} = resolve(event.currentTarget);
            const {draggedHeight} = limits(dragged);

            commit(
                dragged,
                measure(dragged, draggedHeight + direction * step).draggedHeight
            );
        },
        [limits, measure, commit]
    );

    return {dragging, preview, startDrag, nudge};
};
